import { NumberInput, Select, Textarea, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import type { TransferCarrierFormSchema } from "@msuite/utils/schemas";
import { useFormContext } from "react-hook-form";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { useWarehouseZones } from "@/hooks/use-warehouse-zones";

/**
 * The fields of a transfer carrier. Headless, like every form in this directory — the modal
 * around it owns the submit.
 *
 * Four fields, and the absences say more than the presences. There is no balance, no dimensions
 * and no thresholds: a trolley is one individual object rather than a counted type, so nothing
 * here is a quantity. `LoadCarrierForm` next door has all of them, which is the clearest statement
 * of why the two are separate tables.
 *
 * The number is optional, and leaving it empty is the ordinary case for a new device — the
 * database allocates the organisation's next one and somebody prints that label. Filling it in is
 * for a trolley that already has a number painted on its side, which is how an existing fleet is
 * entered.
 *
 * The zone is where the device stands now. It is editable so a trolley pushed somewhere without a
 * scan can be corrected, and the helper text says the part that would otherwise surprise: it is
 * overwritten by the next put-away finished on it.
 */
export function TransferCarrierForm() {
	/** Context */
	const methods = useFormContext<TransferCarrierFormSchema>();
	const mentionOptions = useMentionOptions();

	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { warehouseZones } = useWarehouseZones();

	/** Render */
	return (
		<div className="v-stack gap-4">
			<TextInput
				label={t("general.name")}
				isRequired={false}
				control={methods.control}
				name="name"
				placeholder="Transportwagen 1"
			/>
			<NumberInput
				label={t("stock.transfer_carrier_no")}
				isRequired={false}
				control={methods.control}
				name="number"
				helperText={t("stock.transfer_carrier_number_hint")}
				variant="integer"
				min={1}
				step={1}
			/>
			<Textarea
				disableFormatting
				label={t("general.description")}
				isRequired={false}
				control={methods.control}
				name="description"
				{...mentionOptions}
			/>
			<Select
				control={methods.control}
				name="currentZoneId"
				isRequired={false}
				label={t("stock.transfer_carrier_current_zone")}
				options={warehouseZones.map((warehouseZone) => ({
					label: warehouseZone.name,
					value: warehouseZone.id,
					item: warehouseZone,
				}))}
			/>
		</div>
	);
}
